import { useTranslation } from "react-i18next";
import type { Order } from "@/api.types.v2";

type CreatedOrder = Pick<Order, "orderNumber"> & {
  shipments?: Pick<
    NonNullable<Order["shipments"]>[number],
    "id" | "shipmentNumber"
  >[];
};

type ResultProps = {
  recipientName: string;
  destination: string;
  charge: string;
  order?: CreatedOrder;
};

export function BulkShipmentResults({
  results,
}: {
  results: (ResultProps & { id: string; status: string })[];
}) {
  return (
    <>
      {results
        .filter((result) => result.status === "completed")
        .map((result) => (
          <BulkShipmentResult key={result.id} {...result} />
        ))}
    </>
  );
}

/** One completed bulk result, retaining its order and charge context. */
export function BulkShipmentResult({
  recipientName,
  destination,
  charge,
  order,
}: ResultProps) {
  const { t } = useTranslation();
  const unavailable = t("bulkShip.shipmentReferenceUnavailable");
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-2 border-b last:border-0">
      <div className="min-w-0">
        <p className="font-medium break-words">{recipientName}</p>
        <p className="text-sm text-default-500">{destination}</p>
      </div>
      <div className="min-w-0 text-right">
        <ul
          aria-label={t("bulkShip.shipmentReferences")}
          className="font-mono font-semibold break-all"
        >
          {order?.shipments?.length ? (
            order.shipments.map((shipment) => (
              <li key={shipment.id}>
                {shipment.shipmentNumber != null && shipment.shipmentNumber > 0
                  ? `SHP-${shipment.shipmentNumber}`
                  : unavailable}
              </li>
            ))
          ) : (
            <li>{unavailable}</li>
          )}
        </ul>
        <p className="text-sm text-default-500">
          {order?.orderNumber && order.orderNumber > 0
            ? `ORD-${order.orderNumber}`
            : t("bulkShip.orderReferenceUnavailable")}
        </p>
        <p className="text-sm text-default-500">{charge}</p>
      </div>
    </div>
  );
}
